// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type BaseQueryEnhancer, type FetchArgs } from '@reduxjs/toolkit/query';
import type { AccountCurrency } from '@shared/data-access/types/brokerage-types';

/** Options for configuring the currency query. */
export interface CurrencyQueryOptions {
  /** Determines if the currency should be included in the query. */
  includeCurrency?: boolean;
}

/**
 * Enhances a base query to optionally include currency in the fetch request.
 *
 * @param baseQuery - The base query function to enhance.
 * @param config - Default configuration options for the currency query.
 * @returns A function that executes the enhanced query.
 */
export const currencyQuery: BaseQueryEnhancer<
  FetchArgs,
  CurrencyQueryOptions,
  CurrencyQueryOptions & { selector: (state: any) => AccountCurrency }
> =
  (baseQuery, config) =>
  async (args, api, extraOptions): Promise<any> => {
    const shouldIncludeCurrency =
      extraOptions?.includeCurrency ?? config?.includeCurrency;

    if (shouldIncludeCurrency) {
      const currency = config.selector(api.getState());

      const queryWithCurrency = {
        ...args,
        params: {
          ...args.params,
          currency: currency,
        },
      };

      return baseQuery(queryWithCurrency, api, extraOptions);
    }

    return baseQuery(args, api, extraOptions);
  };
